import { PromotionSkipReason } from '../types/cart'

/**
 * Operator-facing wording for each skip reason.
 *
 * PHRASED AS THE ANSWER TO A QUESTION, not as an enum member. The operator arrived here
 * asking "why is my campaign not applying"; "BUDGET_EXHAUSTED" restates the machine's
 * vocabulary back at them, while "The budget is used up" is the answer. The enum name stays
 * available in the DOM via data attributes for support, so nothing is lost.
 *
 * DELIBERATELY NOT DERIVED from the enum by de-camel-casing. That would look tidy and produce
 * "Per Guest Limit No Guest Id", which is not English, and it would silently invent a label
 * for a reason nobody has thought about — including one added to the contract tomorrow. An
 * explicit map makes an unmapped reason visible instead (see skipReasonLabel).
 */
/**
 * KEYED BY THE UNION, not by `string`.
 *
 * As `Record<string, string>` this map silently disagreed with the contract: it carried
 * `PerGuestLimitNoGuestID` while the schema declares `PerGuestLimitNoGuestId`. One character,
 * one reason, and the `?? reason` fallback below turned it into a raw enum name on screen
 * rather than an error — degrading gracefully into looking slightly wrong, which is why it
 * survived. The reason is not rare: staging returns it for any capped promotion evaluated
 * without a guest identity.
 *
 * Typed against PromotionSkipReason, a wrong key is a compile error and a NEW reason added to
 * the contract fails here until it is given a sentence. Same mechanism that closed the
 * distribution-enum drift in types/cart.ts.
 */
const SKIP_REASON_LABELS: Record<PromotionSkipReason, string> = {
  NotActive: 'Not active — draft, paused, or outside its own dates',
  OutOfStayWindow: 'The stay dates fall outside the promotion',
  OutOfBookingWindow: 'Booked outside the promotion’s booking window',
  BlackoutDate: 'The stay covers a blackout date',
  DayOfWeekMismatch: 'The stay does not include a qualifying day of week',
  AdvancePurchaseMismatch: 'Booked too close to, or too far from, the stay',
  ScopeMismatch: 'Does not apply to this property or rate plan',
  ConsumableViaMismatch: 'Not available through this channel',
  CodeRequired: 'Needs a code, and none was entered',
  CodeInvalid: 'The code entered does not match',
  CodeAlreadyUsed: 'The code entered has already been used',
  CodeAmbiguous: 'The code matches more than one promotion',
  MaxRedemptionsReached: 'The redemption limit is reached',
  BudgetExhausted: 'The budget is used up',
  PerGuestLimitReached: 'This guest has reached their limit',
  PerGuestLimitNoGuestId:
    'Limited per guest, but the booking has no guest identity',
  NotStackable: 'Another promotion applied and these cannot combine',
  EligibilityFailed: 'The booking does not meet the eligibility rules',
  Unknown: 'Unrecognised reason'
}

/**
 * A reason's label, falling back to the raw value rather than to a blank or a guess.
 *
 * The fallback is reachable and is meant to be: the service maps anything it does not
 * recognise to `Unknown`, but a reason added to the contract before this map catches up
 * arrives here as its own enum name. Showing that name is worse-looking and more useful than
 * showing nothing — an operator can quote it to support, and it is obvious something is
 * missing, where a blank cell or an invented label is not.
 */
export const skipReasonLabel = (reason: string): string =>
  // The cast is at the LOOKUP, never at the declaration. `reason` is wire data and may be
  // anything, which is what the fallback below is for; the map's KEYS stay checked against
  // the contract, so a misspelled or missing entry is still a compile error. Casting the
  // declaration instead would restore the exact hole this change closes.
  SKIP_REASON_LABELS[reason as PromotionSkipReason] ?? reason

/**
 * Every reason the map covers, derived rather than hand-listed.
 *
 * The map is `Record<PromotionSkipReason, string>`, so the compiler forces it to be
 * exhaustive. A second hand-written list of the same members is not forced to anything: a
 * reason added to the union would fail to compile in the map and compile fine in the list,
 * so an "every reason resolves" test would silently stop covering the new one. Derived, the
 * exhaustiveness guarantee flows from the one place the compiler already checks.
 */
export const ALL_SKIP_REASONS = Object.keys(
  SKIP_REASON_LABELS
) as PromotionSkipReason[]

/** Every label, for the localization scan — these reach getTranslate through a variable. */
export const ALL_SKIP_REASON_LABELS = Object.values(SKIP_REASON_LABELS)
